import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, PaperPlaneRight, Users, UserPlus, CalendarDots,
  ChatCircle, Gear, SignOut, Crown, Trash, MagnifyingGlass
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const TIME_LABELS = {
  '09:00': '9a', '10:00': '10a', '11:00': '11a', '12:00': '12p',
  '13:00': '1p', '14:00': '2p', '15:00': '3p', '16:00': '4p',
  '17:00': '5p', '18:00': '6p', '19:00': '7p', '20:00': '8p',
  '21:00': '9p', '22:00': '10p'
};

export default function GroupDetailPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [tab, setTab] = useState('chat');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [friends, setFriends] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  const fetchGroup = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/groups/${groupId}`, { withCredentials: true });
      setGroup(data);
    } catch (err) {
      toast.error('Failed to load group');
      navigate('/groups');
    }
  }, [groupId, navigate]);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/groups/${groupId}/messages`, { withCredentials: true });
      setMessages(prev => JSON.stringify(prev) !== JSON.stringify(data) ? data : prev);
    } catch (err) { /* silent */ }
  }, [groupId]);

  useEffect(() => {
    const init = async () => {
      await Promise.all([fetchGroup(), fetchMessages()]);
      setLoading(false);
    };
    init();
  }, [fetchGroup, fetchMessages]);

  // Poll for messages
  useEffect(() => {
    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    setSending(true);
    const content = newMessage.trim();
    setNewMessage('');
    try {
      const { data } = await axios.post(`${API_URL}/api/groups/${groupId}/messages`, { content }, { withCredentials: true });
      setMessages(prev => [...prev, data]);
    } catch (err) { toast.error('Failed to send'); }
    finally { setSending(false); }
  };

  const loadFriends = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/friends`, { withCredentials: true });
      const memberIds = new Set((group?.members || []).map(m => m.id));
      setFriends(data.filter(f => !memberIds.has(f.id)));
    } catch (err) { /* silent */ }
    setShowInvite(true);
  };

  const inviteFriend = async (friendId) => {
    try {
      await axios.post(`${API_URL}/api/groups/${groupId}/invite`, { user_id: friendId }, { withCredentials: true });
      toast.success('Invite sent!');
      setFriends(prev => prev.filter(f => f.id !== friendId));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const leaveGroup = async () => {
    try {
      await axios.post(`${API_URL}/api/groups/${groupId}/leave`, {}, { withCredentials: true });
      toast.success('Left group');
      navigate('/groups');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteGroup = async () => {
    try {
      await axios.delete(`${API_URL}/api/groups/${groupId}`, { withCredentials: true });
      toast.success('Group deleted');
      navigate('/groups');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';

  // Compute shared availability slots
  const getSharedAvailability = () => {
    if (!group || !group.members) return [];
    const membersWithAvail = group.members.filter(m => m.availability && m.availability.length > 0);
    if (membersWithAvail.length < 2) return [];

    const shared = [];
    for (let day = 0; day < 7; day++) {
      const slotCounts = {};
      membersWithAvail.forEach(m => {
        const daySlot = m.availability.find(a => a.day_of_week === day);
        if (daySlot) {
          daySlot.time_slots.forEach(t => {
            slotCounts[t] = (slotCounts[t] || 0) + 1;
          });
        }
      });
      const overlapping = Object.entries(slotCounts)
        .filter(([_, count]) => count >= 2)
        .map(([time, count]) => ({ time, count }));
      if (overlapping.length > 0) {
        shared.push({ day, slots: overlapping });
      }
    }
    return shared;
  };

  if (loading || !group) {
    return (
      <div className="app-container flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-[#9D4CDD] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isOwner = group.owner_id === user?.id;
  const sharedAvail = getSharedAvailability();

  return (
    <div className="app-container flex flex-col" data-testid="group-detail-page">
      {/* Header */}
      <header className="p-4 flex items-center gap-3 border-b border-white/5">
        <button onClick={() => navigate('/groups')} className="text-white" data-testid="back-button">
          <ArrowLeft size={22} weight="bold" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold text-white truncate" style={{ fontFamily: 'Unbounded, sans-serif' }}>{group.name}</h1>
          <p className="text-[#71717A] text-[10px]">{group.member_count} member{group.member_count !== 1 ? 's' : ''}{group.game_type ? ` · ${group.game_type}` : ''}</p>
        </div>
        <button onClick={loadFriends} className="w-9 h-9 rounded-full bg-[#9D4CDD]/20 flex items-center justify-center" data-testid="invite-button">
          <UserPlus size={16} weight="bold" className="text-[#9D4CDD]" />
        </button>
      </header>

      {/* Tabs */}
      <div className="px-4 py-2 flex gap-2 border-b border-white/5">
        {[
          { key: 'chat', label: 'Chat', icon: ChatCircle },
          { key: 'members', label: 'Members', icon: Users },
          { key: 'schedule', label: 'Schedule', icon: CalendarDots },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1 ${
              tab === t.key ? 'bg-[#9D4CDD]/20 text-[#9D4CDD]' : 'text-[#71717A]'
            }`} data-testid={`tab-${t.key}`}>
            <t.icon size={14} weight={tab === t.key ? 'fill' : 'regular'} /> {t.label}
          </button>
        ))}
      </div>

      {/* Invite Modal */}
      <AnimatePresence>
        {showInvite && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 bg-black/70 flex items-end" onClick={() => setShowInvite(false)}>
            <motion.div initial={{ y: 300 }} animate={{ y: 0 }} exit={{ y: 300 }}
              className="w-full max-h-[60vh] rounded-t-3xl bg-[#0F0F17] p-5 overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="invite-modal">
              <p className="text-white font-bold text-base mb-4">Invite Friends</p>
              {friends.length === 0 ? (
                <p className="text-[#71717A] text-sm text-center py-8">No friends available to invite</p>
              ) : (
                friends.map((f, i) => (
                  <div key={f.id} className="flex items-center gap-3 py-3 border-b border-white/5" data-testid={`invite-friend-${i}`}>
                    {f.avatar ? (
                      <img src={f.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#0EA5E9] flex items-center justify-center text-white text-xs font-bold">{getInitials(f.name)}</div>
                    )}
                    <p className="text-white text-sm flex-1">{f.name}</p>
                    <button onClick={() => inviteFriend(f.id)} className="px-3 py-1 rounded-lg bg-[#9D4CDD]/20 text-[#9D4CDD] text-xs font-semibold" data-testid={`send-invite-${i}`}>Invite</button>
                  </div>
                ))
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex-1 overflow-y-auto">
        {/* Chat Tab */}
        {tab === 'chat' && (
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {messages.length === 0 && (
                <div className="text-center py-12 text-[#71717A]">
                  <ChatCircle size={40} weight="duotone" className="mx-auto mb-2 text-[#3F3F46]" />
                  <p className="text-xs">No messages yet. Say hi!</p>
                </div>
              )}
              {messages.map((msg, i) => {
                const isMine = msg.sender_id === user?.id;
                return (
                  <div key={msg.id || i} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] ${isMine ? '' : 'flex gap-2'}`}>
                      {!isMine && (
                        msg.sender_avatar ? (
                          <img src={msg.sender_avatar} alt="" className="w-7 h-7 rounded-full mt-1 shrink-0 object-cover" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] flex items-center justify-center text-white text-[8px] font-bold mt-1 shrink-0">{getInitials(msg.sender_name)}</div>
                        )
                      )}
                      <div>
                        {!isMine && <p className="text-[#71717A] text-[10px] mb-0.5 ml-1">{msg.sender_name}</p>}
                        <div className={`px-3 py-2 rounded-2xl text-sm ${
                          isMine ? 'bg-[#9D4CDD] text-white rounded-br-sm' : 'bg-[#1E1E2E] text-white rounded-bl-sm'
                        }`}>{msg.content}</div>
                        <p className="text-[#52525B] text-[9px] mt-0.5 ml-1">
                          {msg.created_at ? format(new Date(msg.created_at), 'h:mm a') : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={sendMessage} className="p-3 border-t border-white/5 flex gap-2">
              <input type="text" value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Message..."
                className="input-field flex-1 text-sm py-2.5" data-testid="group-message-input" />
              <button type="submit" disabled={!newMessage.trim() || sending}
                className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] flex items-center justify-center disabled:opacity-30" data-testid="send-group-message">
                <PaperPlaneRight size={18} weight="fill" className="text-white" />
              </button>
            </form>
          </div>
        )}

        {/* Members Tab */}
        {tab === 'members' && (
          <div className="px-4 py-3 space-y-2 pb-32">
            {(group.members || []).map((m, i) => (
              <div key={m.id} className="p-3 rounded-xl bg-[#181824] border border-white/5 flex items-center gap-3" data-testid={`member-${i}`}>
                {m.avatar ? (
                  <img src={m.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] flex items-center justify-center text-white text-xs font-bold">{getInitials(m.name)}</div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="text-white font-semibold text-sm">{m.name}</p>
                    {m.role === 'owner' && <Crown size={12} weight="fill" className="text-[#FACC15]" />}
                  </div>
                  <p className="text-[#71717A] text-[10px]">Lv.{m.level || 0} · {m.xp || 0} XP</p>
                </div>
                {/* Availability indicator */}
                {m.availability && m.availability.length > 0 ? (
                  <span className="px-2 py-0.5 rounded-md bg-[#39FF14]/10 text-[#39FF14] text-[9px] font-medium">Available</span>
                ) : (
                  <span className="px-2 py-0.5 rounded-md bg-[#3F3F46]/30 text-[#71717A] text-[9px]">No schedule</span>
                )}
              </div>
            ))}

            {/* Group Actions */}
            <div className="pt-4 space-y-2">
              {!isOwner && (
                <button onClick={leaveGroup} className="w-full py-3 rounded-xl bg-red-500/10 text-red-400 text-sm font-medium flex items-center justify-center gap-2" data-testid="leave-group-button">
                  <SignOut size={16} /> Leave Crew
                </button>
              )}
              {isOwner && (
                <button onClick={deleteGroup} className="w-full py-3 rounded-xl bg-red-500/10 text-red-400 text-sm font-medium flex items-center justify-center gap-2" data-testid="delete-group-button">
                  <Trash size={16} /> Delete Crew
                </button>
              )}
            </div>
          </div>
        )}

        {/* Schedule Tab */}
        {tab === 'schedule' && (
          <div className="px-4 py-3 pb-32 space-y-4">
            {/* Shared Availability */}
            {sharedAvail.length > 0 ? (
              <div>
                <p className="text-xs font-semibold text-[#A1A1AA] mb-3 uppercase tracking-wider">Best Times to Play</p>
                {sharedAvail.map((s, si) => (
                  <div key={si} className="p-3 rounded-xl bg-[#181824] border border-[#39FF14]/20 mb-2" data-testid={`shared-slot-${si}`}>
                    <p className="text-white font-semibold text-sm mb-1.5">{DAYS[s.day]}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {s.slots.map((slot, sli) => (
                        <span key={sli} className={`px-2 py-0.5 rounded-md text-[10px] font-medium ${
                          slot.count >= (group.members?.length || 2) ? 'bg-[#39FF14]/20 text-[#39FF14]' : 'bg-[#FACC15]/10 text-[#FACC15]'
                        }`}>
                          {TIME_LABELS[slot.time] || slot.time} ({slot.count}/{group.members?.length || 0})
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[#71717A]">
                <CalendarDots size={40} weight="duotone" className="mx-auto mb-2 text-[#3F3F46]" />
                <p className="text-sm">No overlapping availability found</p>
                <p className="text-xs mt-1">Members need to set their availability in their profiles</p>
              </div>
            )}

            {/* Per-member availability */}
            <div>
              <p className="text-xs font-semibold text-[#A1A1AA] mb-3 uppercase tracking-wider">Member Schedules</p>
              {(group.members || []).map((m, mi) => (
                <div key={m.id} className="p-3 rounded-xl bg-[#181824] border border-white/5 mb-2" data-testid={`member-schedule-${mi}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {m.avatar ? (
                      <img src={m.avatar} alt="" className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] flex items-center justify-center text-white text-[7px] font-bold">{getInitials(m.name)}</div>
                    )}
                    <p className="text-white text-xs font-semibold">{m.name}</p>
                  </div>
                  {m.availability && m.availability.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {m.availability.map((a, ai) => (
                        <span key={ai} className="px-1.5 py-0.5 rounded bg-[#00F0FF]/10 text-[#00F0FF] text-[9px]">
                          {DAYS[a.day_of_week]}: {a.time_slots.map(t => TIME_LABELS[t] || t).slice(0, 3).join(', ')}{a.time_slots.length > 3 ? '...' : ''}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[#52525B] text-[10px] italic">No availability set</p>
                  )}
                </div>
              ))}
            </div>

            {/* Link to set own availability */}
            <button onClick={() => navigate('/calendar')}
              className="w-full py-3 rounded-xl bg-[#00F0FF]/10 text-[#00F0FF] text-sm font-medium flex items-center justify-center gap-2" data-testid="set-availability-button">
              <CalendarDots size={16} /> Set My Availability
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
